import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import appointmentService from '../../services/appointmentService';

const AppointmentCalendar = ({ onDateSelect, onAddAppointment, refreshKey }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [monthAppointments, setMonthAppointments] = useState([]);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMonth, setActiveMonth] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  useEffect(() => {
    fetchMonthAppointments();
  }, [activeMonth, refreshKey]);

  useEffect(() => {
    fetchDayAppointments();
  }, [selectedDate, refreshKey]);

  const fetchMonthAppointments = async () => {
    try {
      const response = await appointmentService.getMonthAppointments(
        activeMonth.year,
        activeMonth.month
      );
      setMonthAppointments(response.data);
    } catch (error) {
      console.error('Error fetching month appointments:', error);
    }
  };

  const fetchDayAppointments = async () => {
    try {
      setLoading(true);
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await appointmentService.getAppointmentsByDate(dateStr);
      setDayAppointments(response.data);
    } catch (error) {
      console.error('Error fetching day appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  const handleActiveStartDateChange = ({ activeStartDate }) => {
    setActiveMonth({
      year: activeStartDate.getFullYear(),
      month: activeStartDate.getMonth() + 1,
    });
  };

  // Get appointment count for a specific date
  const getAppointmentCount = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return monthAppointments.filter(
      (apt) => apt.appointment_date === dateStr
    ).length;
  };

  // Customize tile content to show appointment count
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const count = getAppointmentCount(date);
      if (count > 0) {
        return (
          <div className="flex justify-center items-center">
            <span className="bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {count}
            </span>
          </div>
        );
      }
    }
    return null;
  };

  // Add custom class to tiles with appointments
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const count = getAppointmentCount(date);
      if (count > 0) {
        return 'has-appointments';
      }
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'border-blue-500 bg-blue-50';
      case 'Completed':
        return 'border-green-500 bg-green-50';
      case 'Missed':
        return 'border-red-500 bg-red-50';
      case 'Cancelled':
        return 'border-gray-500 bg-gray-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Appointment Calendar
        </h2>
        
        <style>{`
          .react-calendar {
            width: 100%;
            border: none;
            font-family: inherit;
          }
          .react-calendar__tile {
            padding: 1em 0.5em;
            position: relative;
          }
          .react-calendar__tile--active {
            background: #4F46E5 !important;
            color: white;
          }
          .has-appointments {
            background: #EEF2FF;
          }
          .react-calendar__tile--now {
            background: #FEF3C7;
          }
          .react-calendar__tile--now:enabled:hover {
            background: #FDE68A;
          }
        `}</style>
        
        <Calendar
          onChange={handleDateChange}
          value={selectedDate}
          tileContent={tileContent}
          tileClassName={tileClassName}
          onActiveStartDateChange={handleActiveStartDateChange}
        />

        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-yellow-200 rounded mr-2"></div>
              <span>Today</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-indigo-600 rounded mr-2"></div>
              <span>Selected</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-indigo-100 rounded mr-2"></div>
              <span>Has Appointments</span>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Day Appointments */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            {selectedDate.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            })}
          </h3>
          <button
            onClick={() => onAddAppointment(selectedDate)}
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            + Add
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : dayAppointments.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No appointments</p>
            <button
              onClick={() => onAddAppointment(selectedDate)}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              Schedule one now
            </button>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {dayAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className={`border-l-4 p-3 rounded-r ${getStatusColor(
                  appointment.status
                )}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      {appointment.appointment_time}
                    </div>
                    <div className="text-sm text-gray-700">
                      {appointment.patient?.name || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {appointment.patient?.phone || ''}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    appointment.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                    appointment.status === 'Completed' ? 'bg-green-100 text-green-800' :
                    appointment.status === 'Missed' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {appointment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentCalendar;